/**
 * PORT-005 §5-§6 — **entrer dans un espace, et en sortir.** Un seul chemin pour
 * les deux écrans qui le font : la page de choix et le sélecteur de la coque.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 LE DÉFAUT FERMÉ (FE-7 / FE-8, revue du 2026-09-11)
 * ═══════════════════════════════════════════════════════════════════════════
 * Les deux copies de `choisir` écrivaient `if (r.ok) { … }` — et rien d'autre.
 * Un espace fermé entre-temps (404), une session expirée (401), une panne :
 * le clic ne faisait RIEN, et le volet du téléphone s'était déjà refermé avant
 * l'appel. Deux copies, deux silences identiques : c'est la factorisation qui
 * garantit qu'un troisième écran n'en recopiera pas un troisième.
 *
 * · 401 → la session est morte : on vide TOUT le cache et on recharge `/espace`
 *   (jamais `/login`, la page du back-office) ;
 * · 403 / 404 → cet accès n'est plus ouvert : on le DIT, et on relit `/me`
 *   pour que la liste cesse de le proposer ;
 * · autre échec → on dit l'échec avec son code, et on relit `/me` aussi.
 *
 * Rend `true` quand l'espace est ouvert — le volet bas ne se ferme qu'alors.
 */
import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { apiFetch } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'

export function useChoisirLEspace({ remplacer = false }: { remplacer?: boolean } = {}) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    return useCallback(
        async (grantId: string, memoriser?: boolean): Promise<boolean> => {
            let r: Response | null = null
            try {
                r = await apiFetch('/api/portal/context/select', {
                    method: 'POST',
                    // ⚠️ Sans `Content-Type`, FastAPI refuse AVANT la route (422)
                    // et le corps de l'erreur est une LISTE.
                    headers: { 'Content-Type': 'application/json' },
                    // ⚠️ `remember` n'est envoyé que s'il a été DÉCIDÉ. Le
                    // serveur lui donne trois valeurs (`true` mémorise, `false`
                    // efface, absent = ne touche à rien) : le sélecteur de la
                    // coque, qui ne propose pas ce choix, doit laisser le défaut
                    // de la personne intact plutôt que l'effacer à chaque bascule.
                    body: JSON.stringify(
                        memoriser === undefined
                            ? { grant_id: grantId }
                            : { grant_id: grantId, remember: memoriser },
                    ),
                })
            } catch {
                r = null
            }
            if (r?.ok) {
                await queryClient.invalidateQueries({ queryKey: queryKeys.portal.all })
                navigate('/espace/app', { replace: remplacer })
                return true
            }
            if (r?.status === 401) {
                queryClient.clear()
                window.location.replace('/espace')
                return false
            }
            const ferme = r?.status === 403 || r?.status === 404
            toast.error(ferme ? t('portal.context.closed') : t('portal.context.error', { code: r?.status ?? 'NET' }))
            await queryClient.invalidateQueries({ queryKey: queryKeys.portal.me() })
            return false
        },
        [queryClient, navigate, remplacer, t],
    )
}

/**
 * Se déconnecter d'un espace. `/api/auth/logout` reste atteignable à tout
 * compte (PORT-002 §4) ; il n'existe aucune route `/api/portal/logout`.
 *
 * ⚠️ On sort MÊME si l'appel échoue : rester coincé dans un espace parce que
 * le réseau a coupé au mauvais moment serait pire qu'un cookie laissé à
 * expirer — le rechargement de `/espace` repassera de toute façon par la sonde.
 */
export function useSeDeconnecterDeLEspace() {
    const queryClient = useQueryClient()
    return useCallback(async () => {
        try {
            await apiFetch('/api/auth/logout', { method: 'POST' })
        } catch {
            // voir ci-dessus
        }
        queryClient.clear()
        window.location.replace('/espace')
    }, [queryClient])
}
